import React, {useEffect, useMemo, useState} from 'react';
import { Card, Table, Badge, Dropdown, Pagination } from 'react-bootstrap';
import {authService} from "../../services/authService";
import NotificationService from "../../services/NotificationService";
import {userService} from "../../services/userService";
import {businessYearService} from "../../services/businessYearService";
import eventEmitter from "../../shared/eventEmitter";
import {EventEmitterTypes} from "../../shared/eventEmitterEnum";
import {useNavigate} from "react-router-dom";
import {CustomToggle} from "../../shared/components/CustomYearlySettingsToggleOptions";
import PriceAdjustmentsModal from "./modals/PriceAdjustmentsModal";
import ConfirmationModal from "../ConfirmationModal";




export interface Settings {
    created: Date,
    current: boolean,
    maximumWorkAmountPerDay: number,
    scheduleEndDate: Date,
    scheduleStartDate: Date,
    year: number
}

interface YearlySettingsDetailsProps {
    details: Settings;
}

interface PriceAdjustment {
    priceAdjustmentId: number;
    name: string;
    startDate: Date;
    endDate: Date;
    amount: number;
    percentage: number;
}

const PriceAdjustmentsCard: React.FC<YearlySettingsDetailsProps> = ({details}) => {
    const [priceAdjustments, setPriceAdjustments] = useState<PriceAdjustment[]>([]);
    const [activeModalData, setActiveModalData] = useState<any>();
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteRecordData, setDeleteRecordData] = useState(null);

    const hasMismatchedYears = useMemo(() => {
        const years = new Set<number>();

        for (const adj of priceAdjustments) {
            const startYear = new Date(adj.startDate).getFullYear();
            const endYear = new Date(adj.endDate).getFullYear();
            years.add(startYear);
            years.add(endYear);
        }

        return years.size > 1;
    }, [priceAdjustments]);

    useEffect(() => {
        const refreshListener = () => {
            businessYearService.getPriceAdjustments(details.year).then(r => setPriceAdjustments(r));
        };

        eventEmitter.on(EventEmitterTypes.REFRESH_LOADED_YEAR_ADJUSTMENTS, refreshListener);

        return () => {
            eventEmitter.off(EventEmitterTypes.REFRESH_LOADED_YEAR_ADJUSTMENTS, refreshListener);
        };
    }, []);
    function addOrdinal(day) {
        if (day >= 11 && day <= 13) {
            return `${day}th`;
        }
        switch (day % 10) {
            case 1:
                return `${day}st`;
            case 2:
                return `${day}nd`;
            case 3:
                return `${day}rd`;
            default:
                return `${day}th`;
        }
    }
    
    function formatDate(dateStr) {
        const date = new Date(dateStr);
        const month = date.toLocaleString('en-US', { timeZone: 'UTC', month: "long" });
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();
        if(hasMismatchedYears)
            return `${month} ${addOrdinal(day)}, ${year}`;
        return `${month} ${addOrdinal(day)}`;
    }
    
    function getAdjustmentAmount(adjustment: PriceAdjustment) {
        if(adjustment.percentage)
            return `${adjustment.percentage}%`
        return `$${adjustment.amount}`
    }

    const deleteRecord = () => {
        businessYearService.deletePriceAdjustment(details.year, deleteRecordData).then(r => eventEmitter.emit(EventEmitterTypes.REFRESH_LOADED_YEAR_ADJUSTMENTS));
    }
    
    useEffect(() => {
        businessYearService.getPriceAdjustments(details.year).then(r => setPriceAdjustments(r));
    }, []);
    return (<>
        <div className="card yearlySettingsCard">
            <div className="cardHeader">
                <h2 className="">
                                    <span className="cuteIcon orange">
                                        <i className="fa-solid fa-money-check-dollar-pen"></i>
                                    </span>
                    Price Adjustments
                </h2>
                <div className="cardAction">
                    <button type="button" className="btn btn-settings" onClick={() => {
                        setActiveModalData(null);
                        setShowModal(true);
                    }}>
                        <i className="fa-regular fa-plus"></i> add
                    </button>
                </div>
            </div>
            <div className="cardTable">
                <table className="table table-sm table-hover align-middle">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Start</th>
                        <th>End</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {priceAdjustments.sort((a, b) =>
                        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()).
                        map(adjustment => {
                            return (
                                <tr>
                                    <td>{adjustment.name}</td>
                                    <td>{formatDate(adjustment.startDate)}</td>
                                    <td>{formatDate(adjustment.endDate)}</td>
                                    <td className="text-end">{getAdjustmentAmount(adjustment)}</td>
                                    <td className="text-end">
                                        <Dropdown style={{display: 'flex', justifyContent: 'flex-end'}} align="end"
                                                  className="text-end">
                                            <Dropdown.Toggle as={CustomToggle} id="priceSqFtBtn">
                                                <i className="fa-solid fa-ellipsis"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setActiveModalData(adjustment);
                                                        setShowModal(true);
                                                    }}
                                                >
                                                    <i className="fa-light fa-pencil fa-fw"></i> Edit
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    className={'text-danger'}
                                                    onClick={() => {
                                                        setShowDeleteModal(true);
                                                        setDeleteRecordData(adjustment.priceAdjustmentId)
                                                        
                                                    }}
                                                >
                                                    <i className="fa-light fa-trash fa-fw"></i> Delete
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
            <PriceAdjustmentsModal year={details.year} show={showModal} toggle={() => setShowModal(false)} details={activeModalData}/>
            <ConfirmationModal show={showDeleteModal} toggle={() => setShowDeleteModal(false)} onConfirm={deleteRecord}/>

        </>
    );
};

export default PriceAdjustmentsCard;
